import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Organization} from '../../../../shared/model/organization';
import {OrganizationService} from '../../../../shared/services/organization.service';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {PaginatedListComponent} from '../../../../shared/components/paginated-list/paginated-list.component';
import {PermissionService} from '../../../../core/services/permission.service';
import {OrganizationCardComponent} from '../organization-card/organization-card.component';
import {EventService} from '../../../../shared/services/event.service';
import {Event} from '../../../../shared/model/event';
import {OsmMapComponent} from '../../../../shared/components/osm-map/osm-map.component';
import {organizationMapMarkers} from '../../../../shared/utils/map-markers';
import {UserGeolocationService} from '../../../../shared/services/user-geolocation.service';
import {compareOrganizationsByDistance} from '../../../../shared/utils/organization-sorting';

@Component({
  selector: 'app-organization-list',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    PaginatedListComponent,
    OrganizationCardComponent,
    OsmMapComponent,
  ],
  templateUrl: './organization-list.component.html',
  styleUrl: './organization-list.component.css',
})
export class OrganizationListComponent implements OnInit {
  protected organizations = signal<Array<Organization>>([]);
  protected events = signal<Array<Event>>([]);
  protected view = signal<'list' | 'map'>('list');
  protected isLoading = signal<boolean>(true);
  protected searchControl = new FormControl<string>('', {nonNullable: true});
  protected searchQuery = signal<string>('');
  private organizationService: OrganizationService = inject(OrganizationService);
  private eventService: EventService = inject(EventService);
  private permissionService = inject(PermissionService);
  private userGeolocation = inject(UserGeolocationService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  protected readonly isMine = this.route.snapshot.data['filter'] === 'mine';
  protected readonly mapMarkers = computed(() =>
    organizationMapMarkers(this.events(), this.organizations()),
  );

  private readonly sortedOrganizations = computed(() => {
    const orgs = this.organizations();
    const evs = this.events();
    const user = this.userGeolocation.position();
    return [...orgs].sort(compareOrganizationsByDistance(user, evs));
  });

  protected readonly visibleOrganizations = computed(() =>
    this.filterOrganizations(this.sortedOrganizations()),
  );

  ngOnInit(): void {
    this.userGeolocation.ensureLocated();

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => this.searchQuery.set(query));

    this.loadOrganizations();
    this.loadEvents();
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  private filterOrganizations(organizations: readonly Organization[]): Organization[] {
    if (this.isMine) {
      return [...organizations];
    }
    const query = this.searchQuery().trim().toLowerCase();
    if (query.length === 0) {
      return [...organizations];
    }
    return organizations.filter((organization) =>
      [organization.organizationName, organization.organizationDescription]
        .filter((value): value is string => !!value)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }

  loadOrganizations() {
    this.isLoading.set(true);
    const request$ = this.isMine
      ? this.organizationService.getMyOrganizations()
      : this.organizationService.getAllOrganizations();

    request$.subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const organizations = this.isMine
            ? response.data
            : response.data.filter((org) => org.organizationActive);
          this.organizations.set(organizations);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  canCreate(): boolean {
    return this.permissionService.canCreateOrganization();
  }

  protected setView(view: 'list' | 'map'): void {
    this.view.set(view);
    if (view === 'map' && this.events().length === 0) {
      this.loadEvents();
    }
  }

  private loadEvents(): void {
    this.eventService.getAllEvents(true).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.events.set(response.data.filter((event) => event.active !== false));
        }
      },
      error: () => {
        this.events.set([]);
      },
    });
  }
}
